import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SITE } from '../mock/data';
import { useLang } from '../i18n';
import { publicApi } from '../api';
import { toAbsoluteMediaUrl } from '../lib/media';

// Same store URLs as /download — kept identical because the QR code is already printed.
const APPLE_URL = 'https://apps.apple.com/af/app/yup-your-utility-points/id6738097216';
const GOOGLE_URL = 'https://play.google.com/store/apps/details?id=yupdesenzano.app';

const APPLE_BADGE =
  'https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83';
const GOOGLE_BADGE =
  'https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png';

export default function DesenzanoLoft() {
  const { t } = useLang();
  const [logos, setLogos] = useState({ desenzanoloft_logo: '', marriott_logo: '' });

  useEffect(() => {
    // Load co-brand partner logos from public settings (managed in Admin CMS).
    publicApi.settings().then(setLogos).catch(() => {});
  }, []);

  // Quotes around the URL are mandatory: the hero contains "(23)" which is invalid
  // in an unquoted CSS url() — the browser drops the whole style attribute otherwise.
  const heroBgCss = `url("${toAbsoluteMediaUrl(SITE.heroBg)}")`;
  const loftLogo = toAbsoluteMediaUrl(logos.desenzanoloft_logo);
  const marriottLogo = toAbsoluteMediaUrl(logos.marriott_logo);

  return (
    <div className="relative w-full" style={{ minHeight: '100vh' }} data-testid="desenzanoloft-page">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: heroBgCss }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/85" />

      <div className="relative z-10 max-w-5xl mx-auto px-6 py-16 text-center text-white flex flex-col items-center">
        <img
          src={SITE.logo}
          alt="YUP logo"
          className="h-28 md:h-40 w-auto drop-shadow-2xl fade-in-up"
          data-testid="loft-logo"
        />

        <h2 className="mt-6 text-sm md:text-base tracking-[0.3em] font-medium text-white/95 fade-in-up fade-in-up-delay-1">
          {t('tagline')}
        </h2>
        <h1 className="mt-3 text-4xl md:text-5xl tagline-display fade-in-up fade-in-up-delay-2">
          {t('welcome')}
        </h1>
        <p className="mt-4 text-base md:text-lg tracking-[0.25em] text-[color:var(--yup-primary)] fade-in-up fade-in-up-delay-2">
          {t('travelWithUs')}
        </p>

        {/* Partner co-brand row (logos configurable from Admin CMS) */}
        <div
          className="mt-12 flex flex-wrap items-center justify-center gap-6 sm:gap-10 md:gap-14 fade-in-up fade-in-up-delay-3"
          data-testid="loft-partners-row"
        >
          {loftLogo ? (
            <div className="bg-white/95 rounded-xl px-5 py-3 shadow-2xl">
              <img
                src={loftLogo}
                alt="Desenzano Loft"
                className="h-20 md:h-24 w-auto object-contain"
                data-testid="partner-logo-desenzanoloft"
              />
            </div>
          ) : (
            <div
              className="h-24 md:h-28 w-40 md:w-48 rounded-md border border-dashed border-white/30 flex items-center justify-center text-xs text-white/60"
              data-testid="partner-logo-desenzanoloft-placeholder"
            >
              Desenzano Loft
            </div>
          )}
          {marriottLogo ? (
            <div className="bg-white/95 rounded-xl px-5 py-3 shadow-2xl">
              <img
                src={marriottLogo}
                alt="Marriott Homes & Villas"
                className="h-20 md:h-24 w-auto object-contain"
                data-testid="partner-logo-marriott"
              />
            </div>
          ) : (
            <div
              className="h-24 md:h-28 w-40 md:w-48 rounded-md border border-dashed border-white/30 flex items-center justify-center text-xs text-white/60"
              data-testid="partner-logo-marriott-placeholder"
            >
              Marriott Homes &amp; Villas
            </div>
          )}
        </div>

        {/* Download badges — matching heights */}
        <div className="mt-12 fade-in-up fade-in-up-delay-3">
          <h3 className="section-title" style={{ color: 'var(--yup-primary)' }}>
            {t('availableOn')}
          </h3>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-8">
            <a
              href={APPLE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="store-badge"
              data-testid="loft-apple-btn"
              aria-label="Download on the Apple App Store"
            >
              <img
                src={APPLE_BADGE}
                alt="Download on the App Store"
                className="h-[54px] w-auto block"
              />
            </a>
            <a
              href={GOOGLE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="store-badge"
              data-testid="loft-google-btn"
              aria-label="Get it on Google Play"
            >
              <img
                src={GOOGLE_BADGE}
                alt="Get it on Google Play"
                className="h-[54px] w-auto block"
              />
            </a>
          </div>
        </div>

        <div className="mt-14 fade-in-up fade-in-up-delay-3">
          <Link
            to="/"
            className="btn-primary inline-block"
            data-testid="loft-back-home-btn"
          >
            {t('discoverSite')}
          </Link>
        </div>
      </div>
    </div>
  );
}
